import {capitalize, isString, truncate} from 'lodash';
import React, {Key, ReactNode} from 'react';
import styled from 'styled-components';

import {useLocalize} from '../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {Localizer} from '../../../../../libs/shared/core/src/helpers/internationalization/localizeTypes';
import {defineMessage} from '../../../../../libs/shared/core/src/helpers/internationalization/message';
import {UnreachableError} from '../../../../../libs/shared/core/src/helpers/types/customErrors';
import {PaletteColorsEnum} from '../../../../../libs/shared/core/src/models/api/paletteModel';
import {TeamAvatar} from '../../../../../libs/shared/ui/src/components/avatar/teamAvatar';
import {TeammateAvatar} from '../../../../../libs/shared/ui/src/components/avatar/teammateAvatar';
import {Icon, IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverCoordinator} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {ColorDot} from '../../../../../libs/shared/ui/src/components/utility/colorDot';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {UserTheme} from '../../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {ModifiersEnum} from '../../../../../srcCommon/inboxes/search/searchModifiersHelpers';
import {
  SearchLocalFilterIcon,
  SearchLocalFilterIconType,
  SearchLocalFilterTypesEnum,
} from '../../../../store/panels/inboxes/parameters/inboxesParametersFiltersIconHelpers';
import {Emoji} from '../../../common/emoji/emoji';
import {SearchHintItemDropdown} from './searchHintItemDropdown';
import {SearchHintItemPill, SearchHintItemPillTitle} from './searchHintItemPill';
import {isLocationHint, VisualSearchHint, VisualSearchHintValue} from './searchHintsTypes';

/*
 * Props.
 */

interface SearchHintItemProps<T extends VisualSearchHint> {
  hint: T;
  isDisabled: boolean;
  onClick: (hint: T, valueIndex: number) => void;
}

/*
 * Styles.
 */

const StyledFlexDiv = styled.div`
  display: flex;
`;

/*
 * Component.
 */

export const SearchHintItem = <T extends VisualSearchHint>(props: SearchHintItemProps<T>) => {
  const {hint, isDisabled, onClick} = props;
  const {values} = hint;

  const localize = useLocalize();
  const theme = useUserTheme();

  const interactionId = `search-hint-${hint.key}`;
  const isActive = hint.values.some((value) => value.isSelected);

  if (values.length === 1) {
    return (
      <SearchHintItemPill
        onClick={() => onClick(hint, 0)}
        isActive={isActive}
        isDisabled={isDisabled}
        interactionId={interactionId}
      >
        <StyledFlexDiv>{renderTitleForHint(hint, theme, localize)}</StyledFlexDiv>
      </SearchHintItemPill>
    );
  }

  return (
    <PopoverCoordinator
      renderButton={(isOpen) => (
        <SearchHintItemPill
          isActive={isActive}
          isDisabled={isDisabled}
          isHovered={isOpen}
          interactionId={interactionId}
        >
          <StyledFlexDiv>{renderTitleForHint(hint, theme, localize)}</StyledFlexDiv>
        </SearchHintItemPill>
      )}
      renderModal={(popoverProps) => (
        <SearchHintItemDropdown {...popoverProps} hint={hint} onItemClick={(index) => onClick(hint, index)} />
      )}
    />
  );
};

/*
 * Helpers.
 */

function renderTitleForHint(hint: VisualSearchHint, theme: UserTheme, localize: Localizer): ReactNode {
  const defaultTruncationLength = 21;
  const longTruncationLength = 31;

  const isHintSelected = hint.values.some((value) => value.isSelected);

  // We have to flatten the type or we won't be able to use filter on hint.values.
  // This is a common TS problem, see https://github.com/microsoft/TypeScript/pull/53489.
  const flatTypeValues: ReadonlyArray<VisualSearchHintValue> = hint.values;

  // Default to joining the values' titles and icons.
  const defaultTitle = flatTypeValues
    // If no values are selected, display all of them.
    .filter(({isSelected: isValueSelected}) => !isHintSelected || isValueSelected)
    .flatMap(({title, icon, isSelected: isValueSelected, key}, index) => [
      index > 0 ? <>,&nbsp;</> : null,
      renderHintTitleWithIcon(
        maybeTruncateString(title, defaultTruncationLength),
        icon,
        isValueSelected,
        theme,
        key,
      ),
    ]);

  if (isLocationHint(hint)) {
    return defaultTitle;
  }

  const commonModifier =
    hint.values.every((value) => value.modifier === hint.values[0].modifier) && hint.values[0].modifier;

  // If we got a modifier hint with mixed modifiers, use the default title.
  // Should not happen in practice.
  if (!commonModifier) {
    return defaultTitle;
  }

  const displayedHintValues = isHintSelected ? hint.values.filter((value) => value.isSelected) : hint.values;

  // Some hints override the default behavior.
  switch (commonModifier) {
    case ModifiersEnum.TO:
    case ModifiersEnum.FROM: {
      const iconForRecipientHints: SearchLocalFilterIconType = {
        type: SearchLocalFilterTypesEnum.ICON,
        iconName: 'participantSmall',
        selectedIconName: 'participantFilledSmall',
      };

      // We can show more of the recipient if there's only one value.
      const valueTruncationLimit = displayedHintValues.length > 1 ? 12 : longTruncationLength;
      const hintTitle = `${capitalize(commonModifier)} ${displayedHintValues
        .map(({title}) => maybeTruncateString(title, valueTruncationLimit))
        .join(' or ')}`;

      return renderHintTitleWithIcon(
        localize(
          defineMessage({
            id: `search-hint-${commonModifier}`,
            message: maybeTruncateString(hintTitle, longTruncationLength),
          }),
        ),
        iconForRecipientHints,
        isHintSelected,
        theme,
      );
    }

    case ModifiersEnum.TAG: {
      // If we have multiple selected values, it means we matched a tag that has the same alias across multiple namespaces.
      // Only return the name of the tag once.
      // FIXME: We should also check if the displayedHintValues are for the same tags.
      if (isHintSelected && displayedHintValues.length > 1) {
        const multipleTagsIcon: SearchLocalFilterIconType = {
          type: SearchLocalFilterTypesEnum.ICON,
          iconName: 'tagsSmall',
          selectedIconName: 'tagsFilledSmall',
        };

        return renderHintTitleWithIcon(
          maybeTruncateString(displayedHintValues[0].title, longTruncationLength),
          multipleTagsIcon,
          isHintSelected,
          theme,
        );
      }

      return defaultTitle;
    }

    case ModifiersEnum.HAS: {
      // Ignore truncation for the `Exclude calendar updates` hint.
      if (displayedHintValues.length === 1 && displayedHintValues[0].value === 'no-calendar') {
        return renderHintTitleWithIcon(displayedHintValues[0].title, undefined, isHintSelected, theme);
      }

      return defaultTitle;
    }

    default:
      return defaultTitle;
  }
}

function renderHintTitleWithIcon(
  title: string,
  icon: SearchLocalFilterIconType | undefined,
  isActive: boolean,
  theme: UserTheme,
  key?: Key,
) {
  const hintPillIcon = maybeRenderHintPillIcon(icon, isActive, theme);

  return <SearchHintItemPillTitle key={key} icon={hintPillIcon} title={title} />;
}

// TODO: Remove overload after we remove the cast in makeSearchFiltersVisualHint.
export function maybeTruncateString(value: string, maxChars: number): string;
export function maybeTruncateString(value: ReactNode, maxChars: number) {
  if (!isString(value)) {
    return value;
  }

  return truncate(value, {length: maxChars, omission: '…'});
}

function maybeRenderHintPillIcon(
  icon: SearchLocalFilterIconType | undefined,
  isActive: boolean,
  theme: UserTheme,
) {
  if (!icon) {
    return null;
  }

  switch (icon.type) {
    case SearchLocalFilterTypesEnum.EMOJI:
      return <Emoji {...icon.emoji} size={16} />;

    case SearchLocalFilterTypesEnum.ICON:
      return (
        <Icon
          name={maybeReplaceSearchLocalFilterIcon(icon, isActive)}
          color={getIconColor(icon, isActive, theme)}
        />
      );

    case SearchLocalFilterTypesEnum.DOT: {
      return (
        <ColorDot
          size={8}
          color={icon.color ?? theme.greys.shade40}
          hoverColor={icon.color || theme.greys.shade50}
        />
      );
    }

    case SearchLocalFilterTypesEnum.TEAM:
      return <TeamAvatar team={icon.team} size={VisualSizesEnum.SMALL} />;

    case SearchLocalFilterTypesEnum.TEAMMATE:
      return <TeammateAvatar teammate={icon.teammate} size={VisualSizesEnum.SMALL} />;

    default:
      throw new UnreachableError(icon);
  }
}

function maybeReplaceSearchLocalFilterIcon(icon: SearchLocalFilterIcon, isActive: boolean): IconName {
  const {iconName, selectedIconName} = icon;

  if (isActive) {
    return selectedIconName || 'checkmarkSelectedSmall';
  }

  return iconName;
}

function getIconColor(icon: SearchLocalFilterIcon, isActive: boolean, theme: UserTheme) {
  if (isActive) {
    return theme.palette[icon.color ?? PaletteColorsEnum.BLUE].shade40;
  }

  return theme.greys.shade80;
}
